/**
 * queue/surfaces.ts
 * The surface registry + visibility rules.
 *
 * rev 5 reframe — SK Queue is the ENGINE. The admin nav shows only ENGINE
 * surfaces: **Dashboard** (home + queue registry + request-a-queue) and
 * **Queue Detail** (one page, sub-tabs Overview / Controls / Setup). The old
 * operational screens — Guest, Host Console, Now Serving, SK Voice, SK Chat —
 * are PRODUCT SURFACES: they are not nav items, they open only as PREVIEWS from
 * a queue's Overview ("built by the consuming product on the API"). Public
 * previews (Guest, Now Serving) additionally render standalone via their own
 * ?surface= URLs (QR / door display).
 *
 * Visibility = access level (role) ∩ entitlements (subscribed products). One function
 * the navigation + sidebar consult, so gating is consistent and testable.
 */

import type { AccessLevel, Entitlements } from '../context/sessionContext';

export type SurfaceKey =
  // engine surfaces (the only admin nav items)
  | 'dashboard' | 'detail'
  // product surfaces (previews only; not nav items)
  | 'guest' | 'board'               // public previews (also standalone)
  | 'host'                          // staff preview
  | 'voice' | 'chat';               // pool-product previews

export type Product = 'engine' | 'waitlist' | 'voice' | 'chat';

export interface SurfaceDef {
  key: SurfaceKey;
  label: string;
  kicker: string;        // short plain-English caption shown in nav (no tier jargon)
  product: Product;
  /** Public surfaces render standalone (QR / display) — no auth, no shell. */
  public: boolean;
  /** Engine surfaces are the admin nav; everything else is a preview. */
  engine: boolean;
}

// Setup/Controls/Overview are NOT surfaces — they are sub-tabs inside the single
// `detail` surface (the ex-Config/Operator/LiveView screens fold in there). The
// tier concept survives only as a permission gate (canEditTier1), never as UI
// wording.
export const SURFACES: SurfaceDef[] = [
  { key: 'dashboard', label: 'Dashboard',    kicker: 'ENGINE',  product: 'engine',   public: false, engine: true },
  { key: 'detail',    label: 'Queue Detail', kicker: 'QUEUE',   product: 'engine',   public: false, engine: true },
  // product-surface previews
  { key: 'guest',     label: 'Guest',        kicker: 'MOBILE',  product: 'waitlist', public: true,  engine: false },
  { key: 'host',      label: 'Host Console', kicker: 'TABLET',  product: 'waitlist', public: false, engine: false },
  { key: 'board',     label: 'Now Serving',  kicker: 'DISPLAY', product: 'waitlist', public: true,  engine: false },
  { key: 'voice',     label: 'SK Voice',     kicker: 'CALLS',   product: 'voice',    public: false, engine: false },
  { key: 'chat',      label: 'SK Chat',      kicker: 'CHATS',   product: 'chat',     public: false, engine: false },
];

const SURFACE_BY_KEY: Record<SurfaceKey, SurfaceDef> =
  SURFACES.reduce((acc, s) => { acc[s.key] = s; return acc; }, {} as Record<SurfaceKey, SurfaceDef>);

export function getSurface(key: SurfaceKey): SurfaceDef {
  return SURFACE_BY_KEY[key];
}

/**
 * Which product a preview belongs to (for the "open preview" list). Engine
 * surfaces have no product gate.
 */
export function surfaceProduct(key: SurfaceKey): Product {
  return SURFACE_BY_KEY[key].product;
}

// Which ENGINE surfaces an access level may reach. Only Softknack + the client admin
// operate the engine; staff/public reach product surfaces via explicit URLs.
const ACCESS_ENGINE: Record<AccessLevel, SurfaceKey[]> = {
  softknack: ['dashboard', 'detail'],
  client:    ['dashboard', 'detail'],
  staff:     [],
  public:    [],
};

/**
 * Engine surfaces visible to the access level (rev 5 admin nav). Entitlements gate
 * which product PREVIEWS are offered, but the engine surfaces themselves are
 * always shown to an engine access level (the Dashboard lists whatever queues exist).
 */
export function visibleSurfaces(access: AccessLevel, _ent: Entitlements): SurfaceKey[] {
  return ACCESS_ENGINE[access];
}

/** The admin-shell nav = the engine surfaces for this access level. */
export function visibleAdminSurfaces(access: AccessLevel, ent: Entitlements): SurfaceKey[] {
  return visibleSurfaces(access, ent);
}

/**
 * Product-surface previews an admin can open from a queue's Overview. Gated by
 * the org's entitlement + the active queue's product so we only offer previews
 * that make sense (a POOL queue offers Voice/Chat monitors, a FITTED queue the
 * Guest/Host/Board previews).
 */
export function previewSurfacesFor(
  queueProduct: 'WAITLIST' | 'VOICE' | 'CHAT' | undefined,
  ent: Entitlements,
): SurfaceKey[] {
  return SURFACES.filter((s) => {
    if (s.engine) return false;
    if (s.product === 'waitlist') return ent.waitlist && (!queueProduct || queueProduct === 'WAITLIST');
    if (s.product === 'voice') return ent.voice && (!queueProduct || queueProduct === 'VOICE');
    if (s.product === 'chat') return ent.chat && (!queueProduct || queueProduct === 'CHAT');
    return false;
  }).map((s) => s.key);
}

/**
 * Public previews (Guest QR, Now Serving display) that also render standalone.
 * Kept for the topbar "open public preview" launcher.
 */
export function previewablePublicSurfaces(ent: Entitlements): SurfaceKey[] {
  return SURFACES
    .filter((s) => s.public && (s.product !== 'waitlist' || ent.waitlist))
    .map((s) => s.key);
}
